import { useGetPaymentInfoPhoenix } from '@pflegenavi/frontend/api-nursing-home';
import type { PendingCharge as ApiPendingCharge } from '@pflegenavi/frontend/api-nursing-home';
import {
  PendingChargeStatus,
  SourceOfMandate,
  TransactionInitiatedBy,
} from '@pflegenavi/shared/api';
import { PaymentInitiatedBy } from '@pflegenavi/shared/api';
import { useUpcomingPaymentDays } from '@pflegenavi/shared/frontend';
import { useMemo } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { PaymentViewPendingCharge } from '../types';
import { formatPaymentType } from '../formatters';

interface PendingPaymentsPrams {
  resident?: {
    id: string;
  };
}

const isFailedPayment = (pendingChargeStatus: PendingChargeStatus): boolean => {
  return (
    pendingChargeStatus === PendingChargeStatus.failed ||
    pendingChargeStatus === PendingChargeStatus.frozen
  );
};

export function usePendingPayments({
  resident,
}: PendingPaymentsPrams): PaymentViewPendingCharge[] | null {
  const { data: paymentInfoPhoenix } = useGetPaymentInfoPhoenix(
    resident?.id ?? ''
  );

  if (
    !resident ||
    !paymentInfoPhoenix?.pending_charges ||
    paymentInfoPhoenix.pending_charges.length <= 0
  ) {
    return null;
  }

  return paymentInfoPhoenix.pending_charges;
}

export interface PaymentData {
  color: 'success' | 'error' | 'info';
  header: string;
  status: string;
  description: string;
  icon: string;
  reactivatePaymentIcon: boolean;
}

export const countPaymentData = (
  charge: PaymentViewPendingCharge,
  resident: {
    id: string;
    familyMemberId?: string;
  },
  t: TFunction<'translation', undefined>,
  daysLeftToPayment: number
): PaymentData => {
  const isFailed = isFailedPayment(charge.pending_charge_status);
  const isFmPayment =
    charge.payment_initiation === PaymentInitiatedBy.FamilyMember;

  const icon =
    charge.payment_initiation === PaymentInitiatedBy.FamilyMember
      ? 'eva:people-fill'
      : charge.payment_initiation === PaymentInitiatedBy.Employee
      ? 'eva:person-fill'
      : 'eva:settings-2-fill';

  // Map PaymentInitiatedBy to TransactionInitiatedBy for formatPaymentType
  let transactionInitiatedBy: TransactionInitiatedBy;
  if (charge.payment_initiation === PaymentInitiatedBy.FamilyMember) {
    transactionInitiatedBy = TransactionInitiatedBy.FamilyMember;
  } else if (
    charge.payment_initiation === PaymentInitiatedBy.Employee ||
    charge.payment_initiation === PaymentInitiatedBy.Disabled
  ) {
    transactionInitiatedBy = TransactionInitiatedBy.Employee;
  } else {
    transactionInitiatedBy = TransactionInitiatedBy.System;
  }

  // Use formatter to get consistent payment type text
  const paymentTypeText = formatPaymentType(
    undefined,
    transactionInitiatedBy,
    t
  );

  if (isFailed) {
    // payment is failed
    return {
      color: 'error',
      header: paymentTypeText,
      status: 'mobile.family-member.failed-payment-text',
      description: isFmPayment
        ? 'nh-web-app.pages.resident.account.info-cards.fm-failed-payment-subline'
        : 'direct-debit.auto-payments-disabled',
      icon: icon,
      reactivatePaymentIcon: true,
    };
  }

  if (charge.pending_charge_status === PendingChargeStatus.pending) {
    // payment is ongoing
    return {
      color: 'success',
      header: paymentTypeText,
      status: isFmPayment
        ? 'nh-web-app.pages.resident.account.info-cards.fm-payment-in-progress'
        : 'mobile.family-member.payment-in-progress',
      description: isFmPayment
        ? 'nh-web-app.pages.resident.account.info-cards.fm-payment-initiated'
        : charge.source_of_mandate === SourceOfMandate.NursingHome ||
          charge.source_of_mandate === SourceOfMandate.LegalEntity
        ? 'payments.payment-initiated'
        : 'payments.payment-initiated-fm',
      icon: icon,
      reactivatePaymentIcon: false,
    };
  }

  // payment is upcoming
  return {
    color: 'info',
    header: paymentTypeText,
    status:
      daysLeftToPayment > 0
        ? t('payments.upcoming-payment', { count: daysLeftToPayment })
        : t('payments.upcoming-payment_zero'),
    description: '',
    icon: icon,
    reactivatePaymentIcon: false,
  };
};

export const usePaymentData = (
  charge: PaymentViewPendingCharge,
  resident: {
    id: string;
    familyMemberId?: string;
  }
): PaymentData => {
  const { t } = useTranslation();

  const isFailed = isFailedPayment(charge.pending_charge_status);
  const isFmPayment =
    charge.payment_initiation === PaymentInitiatedBy.FamilyMember;

  const { daysLeftToPayment } = useUpcomingPaymentDays({
    chargeDate: charge.charge_date,
  });

  const data = useMemo((): PaymentData => {
    return countPaymentData(charge, resident, t, daysLeftToPayment);
  }, [
    isFailed,
    isFmPayment,
    charge.pending_charge_status,
    charge.source_of_mandate,
    charge.payment_initiation,
    daysLeftToPayment,
    t,
  ]);

  return data;
};
